import { Suspense, lazy, useEffect, useState } from 'react'
import { BrowserRouter, Navigate, Route, Routes, useParams } from 'react-router-dom'

import { Toaster } from '@/components/ui/sonner'
import { CatalogProvider } from '@/catalog/CatalogProvider'
import AppShell from '@/components/AppShell'
import { Page } from '@/components/layout/Page'
import Login from '@/pages/Login'
import SettingsTokens from '@/pages/Settings/Tokens'
import SettingsMcpTokens from '@/pages/Settings/McpTokens'
import SettingsCache from '@/pages/Settings/Cache'
import AuditList from '@/pages/Audit/List'
import AuditInsights from '@/pages/Audit/Insights'
import AuditCompare from '@/pages/Audit/Compare'
import AuditRun from '@/pages/Audit/Run'
import OperationsList from '@/pages/Operations/OperationsList'
import OperationPage from '@/pages/Operations/OperationPage'
import UsersList from '@/pages/Users/UsersList'
import UserDetail from '@/pages/Users/UserDetail'
import ProjectsList from '@/pages/Projects/ProjectsList'
import ProjectDetail from '@/pages/Projects/ProjectDetail'
import ApprovalsPage from '@/pages/Approvals'
import PanelsList from '@/pages/Panels/PanelsList'
import PanelForm from '@/pages/Panels/PanelForm'
import TgeMigrationPage from '@/pages/Tge/TgeMigration'
import FieldCopyPage from '@/pages/FieldCopy/FieldCopyPage'
import { fetchMe, type Me } from '@/lib/auth'

const ApiDocsPage = lazy(() => import('@/pages/ApiDocs/ApiDocsPage'))
const UserUsage = lazy(() => import('@/pages/Analysis/UserUsage'))

// The old API Explorer lives in Operations → All Atlassian API; its bookmarks land there.
const atlassian = (extra: Record<string, string | undefined> = {}) => {
  const q = new URLSearchParams({ mode: 'atlassian' })
  for (const [k, v] of Object.entries(extra)) if (v) q.set(k, v)
  return `/operations?${q.toString()}`
}

function ExplorerCategoryRedirect() {
  const { product, tag } = useParams<{ product: string; tag: string }>()
  return <Navigate replace to={atlassian({ product, tag })} />
}

function ExplorerEndpointRedirect() {
  const { product, operationId } = useParams<{ product: string; operationId: string }>()
  return <Navigate replace to={atlassian({ product, op: operationId })} />
}

function RouteFallback() {
  return (
    <div className="space-y-2 p-4" aria-busy="true" aria-label="Loading page">
      <div className="skeleton" style={{ width: 220, height: 14 }} />
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="skeleton" style={{ width: '100%', height: 22 }} />
      ))}
    </div>
  )
}

function OwnerOnly() {
  return (
    <Page title="Field copy">
      <p className="p-4 text-[13px] text-text-2">Field copy needs the owner role.</p>
    </Page>
  )
}

export default function App() {
  const [me, setMe] = useState<Me | null | undefined>(undefined)

  useEffect(() => {
    fetchMe().then(setMe)
  }, [])

  if (me === undefined) {
    return null // still loading
  }

  return (
    <>
    <Toaster richColors position="bottom-right" />
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <Routes>
        <Route
          path="/login"
          element={me ? <Navigate to="/operations" replace /> : <Login />}
        />
        {me ? (
          <Route
            element={
              <CatalogProvider>
                <AppShell me={me} />
              </CatalogProvider>
            }
          >
            <Route path="/" element={<Navigate to="/operations" replace />} />
            <Route path="/operations" element={<OperationsList />} />
            <Route path="/operations/:operationId" element={<OperationPage />} />
            <Route path="/users" element={<UsersList />} />
            <Route path="/users/:accountId" element={<UserDetail />} />
            <Route path="/projects" element={<ProjectsList />} />
            <Route path="/projects/:projectKey" element={<ProjectDetail />} />
            <Route path="/analysis" element={<Navigate to="/analysis/user" replace />} />
            <Route
              path="/analysis/user/:accountId?"
              element={<Suspense fallback={<RouteFallback />}><UserUsage /></Suspense>}
            />
            <Route
              path="/reference"
              element={
                <Suspense fallback={<RouteFallback />}>
                  <ApiDocsPage />
                </Suspense>
              }
            />
            <Route path="/panels" element={<PanelsList />} />
            <Route path="/panels/new" element={<PanelForm />} />
            <Route path="/panels/:panelId" element={<PanelForm />} />
            <Route path="/approvals" element={<ApprovalsPage />} />
            <Route path="/explorer" element={<Navigate replace to={atlassian()} />} />
            <Route path="/explorer/op/:product/:operationId" element={<ExplorerEndpointRedirect />} />
            <Route path="/explorer/:product/:tag" element={<ExplorerCategoryRedirect />} />
            <Route path="/explorer/*" element={<Navigate replace to={atlassian()} />} />
            <Route path="/audit" element={<AuditList />} />
            <Route path="/audit/insights" element={<AuditInsights />} />
            <Route path="/audit/compare" element={<AuditCompare />} />
            <Route path="/audit/:runId" element={<AuditRun />} />
            <Route path="/tge" element={<TgeMigrationPage />} />
            <Route
              path="/field-copy"
              element={me.role === 'owner' ? <FieldCopyPage /> : <OwnerOnly />}
            />
            <Route path="/settings/tokens" element={<SettingsTokens />} />
            <Route path="/settings/mcp-tokens" element={<SettingsMcpTokens />} />
            <Route path="/settings/cache" element={<SettingsCache />} />
            <Route path="*" element={<Navigate to="/operations" replace />} />
          </Route>
        ) : (
          <Route path="*" element={<Navigate to="/login" replace />} />
        )}
      </Routes>
    </BrowserRouter>
    </>
  )
}
